import React, { useEffect, useState } from 'react'
import { EmtButton, EmtCard, EmtDrawer, EmtIcon, EmtMap, EmtTypography, MarkerList } from 'emt-components-v2'
import Box from '@mui/material/Box'
import { styled, SxProps } from '@mui/material/styles'
import { MapFinderCoordinates } from './Map'
import { Constants } from '@/assets'
import ProfileCardV2 from '@/components/EMT/ProfileCardV2'
import { useTranslation } from 'next-i18next'
import useFilters from '@/hooks/useFilters'
import FiltersButton from '@/components/search/FiltersButton'
import Backdrop from '@mui/material/Backdrop'
import { LinearProgress } from '@mui/material'
import Skeleton from '@mui/material/Skeleton'
import { UserCard } from '@/dataV3/Search/UserCard'
import { SearchFiltersValues } from '@/modules/Search/apiTypes/base'

type Props = {
  homeMarker: React.ReactNode
  markers: MarkerList
  coordinates: MapFinderCoordinates
  profiles: UserCard[]
  selectedProfileId?: number | string
  isLoading: boolean
  onClose?: () => void
  handleMapDragEnd: (lat: number, lng: number) => void
  handleMapZoom?: (zoom: number) => void
  handleCardMouseEnter: (id: number | string) => void
  handleCardMouseLeave: () => void
  setSelectedProfileId: (id?: string | number) => void
  onFiltersChange: (filters: SearchFiltersValues) => void
  onSearchAreaClick: () => void
  handleCardClick: (id: string | number) => void
}

const scrollBarStyle: SxProps = {
  '&::-webkit-scrollbar': {
    width: '5px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#D2D1D4',
    borderRadius: '8px',
  },
  scrollbarColor: '#D2D1D4 transparent',
  scrollbarWidth: '5px',
}

const MapFinderContainer = styled(Box)(() => ({
  position: 'fixed',
  zIndex: 11,
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90vw',
  height: '85vh',
  '&.inner-content': {
    background: 'white',
    borderRadius: '18px',
    overflow: 'hidden',
  },
}))

/**
 * ? 📺 MapDesktopFinder
 */
const MapDesktop: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation('search')

  const {
    homeMarker,
    markers,
    coordinates,
    profiles,
    selectedProfileId,
    onClose,
    handleMapDragEnd,
    handleCardMouseEnter,
    handleCardMouseLeave,
    setSelectedProfileId,
    onSearchAreaClick,
    handleMapZoom,
    isLoading,
    onFiltersChange,
    handleCardClick,
  } = props

  const [activeSearchButton, setActiveSearchButton] = useState(false)
  const [firstLoad, setFirstLoad] = useState(true)

  // [HOOK] Filters
  const { filters, totalFilters, activeFilterSidebar, handleActiveFilter, FiltersComponent } = useFilters({
    activeLocalStorage: true,
    localStorageFiltersKey: 'filters_map',
    localStorageShortFiltersKey: 'shortFilter_map',
    filtersComponentProps: { variant: undefined, sx: { zIndex: 20, paddingLeft: '20px' } },
    disabledFilters: ['distance'],
  })

  const onMapZoom = (zoom, coordinates: { lat: number; lng: number }) => {
    setActiveSearchButton(true)
    handleMapDragEnd(coordinates.lng, coordinates.lat)
    if (handleMapZoom) handleMapZoom(zoom)
  }

  useEffect(() => {
    if (!filters) return
    onFiltersChange(filters)
  }, [filters])

  useEffect(() => {
    setFirstLoad(false)
  }, [])

  useEffect(() => {
    if (firstLoad) return
    setActiveSearchButton(true)
  }, [coordinates])

  useEffect(() => {
    if (firstLoad) return
    !isLoading && setActiveSearchButton(false)
  }, [isLoading])

  return (
    <MapFinderContainer>
      <EmtIcon
        className="emt-x"
        sx={{
          position: 'absolute',
          top: '-30px',
          right: 0,
          color: 'white !important',
          zIndex: 12,
          fontSize: '20px',
          cursor: 'pointer',
        }}
        onClick={onClose}
      />

      <MapFinderContainer className="inner-content">
        <Box
          sx={{
            overflow: 'hidden !important',
            width: '100%',
            height: '100%',
            borderRadius: '18px',
            position: 'absolute',
          }}
        >
          {/* MAP */}
          <Box
            sx={{
              width: '100%',
              height: '100%',
              position: 'relative',
              paddingRight: '370px',
              '& .mapboxgl-ctrl-top-right': {
                marginRight: '10px',
                marginTop: '0px',
              },
              '& .img-marker': {
                maxWidth: '40px !important',
              },
            }}
          >
            <EmtMap
              zoom={16}
              lng={coordinates.lng}
              lat={coordinates.lat}
              onMapDragEnd={(lng, lat) => {
                setActiveSearchButton(true)
                handleMapDragEnd(lng, lat)
              }}
              onMapZoomEnd={onMapZoom}
              homeMarker={homeMarker}
              markerList={markers}
              accessToken={Constants.mapboxAccessToken}
              onMarkerMouseEnter={setSelectedProfileId}
              onMarkerMouseLeave={setSelectedProfileId}
              minZoom={5}
              maxZoom={17}
              navigationControl
            />
            {activeSearchButton ? (
              <Box
                sx={{
                  position: 'absolute',
                  top: 34,
                  zIndex: 11,
                  left: 'calc(50% - 250px)',
                  transform: 'translateX(calc(-50% + 90px))',
                }}
              >
                <EmtButton
                  sx={{ padding: '6px', borderRadius: '12px', fontSize: '12px', '& .emt-icons': { width: 10 } }}
                  startIcon={<EmtIcon className="emt-anti-clockwise-1" />}
                  loader={isLoading}
                  onClick={onSearchAreaClick}
                >
                  {t('map.buttons.searchArea')}
                </EmtButton>
              </Box>
            ) : null}
          </Box>
          {/* END MAP */}

          {/* LOADER */}
          {isLoading ? (
            <>
              <Backdrop
                open={true}
                sx={{ zIndex: 2, background: (theme: any) => theme.palette.common.white, opacity: '0.6 !important' }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: 'calc(50% - 250px)',
                  transform: 'translate(calc(-50% + 90px), -50%)',
                  top: '50%',
                  width: '20%',
                  zIndex: 11,
                }}
              >
                <LinearProgress color="primary" sx={{ height: 5 }} />
              </Box>
            </>
          ) : null}
          {/* END LOADER */}

          {/* FILTER */}
          {FiltersComponent()}
          {activeFilterSidebar ? <Backdrop open={true} sx={{ zIndex: 12 }} onClick={handleActiveFilter} /> : null}

          <Box sx={{ position: 'absolute', top: 30, left: 25, zIndex: 11 }}>
            <FiltersButton onClick={handleActiveFilter} count={filters ? totalFilters : undefined} />
          </Box>
          {/* END FILTER */}

          {/* PROFILES  */}
          <EmtDrawer
            variant="permanent"
            anchor="right"
            open={true}
            id="mapdrawer-cards-wrapper"
            sx={{
              paddingLeft: '0',
              paddingRight: '0',
              borderTopRightRadius: '18px',
              borderTopLeftRadius: 0,
              borderBottomRightRadius: '18px',
              zIndex: 11,
              ...scrollBarStyle,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                rowGap: '10px',
                padding: '10px',
                paddingTop: '15px',
                paddingBottom: '30px',
              }}
            >
              {isLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Skeleton variant="text" height={24} />
                  <Skeleton variant="text" height={24} width="80%" />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <EmtTypography variant="h5" fontWeight={700}>
                    {t(`map.totalfinding.title`, { count: profiles.length })}
                  </EmtTypography>
                  <EmtTypography variant="caption" fontWeight={600}>
                    {t(`map.totalfinding.subtitle`)}
                  </EmtTypography>
                </Box>
              )}
              {isLoading
                ? [...Array(5)].map((v, k) => (
                    <div key={k} style={{ minWidth: '340px' }}>
                      <EmtCard variant="inline" loader />
                    </div>
                  ))
                : profiles.map((profile, k) => (
                    <ProfileCardV2
                      key={k}
                      variant="inlineOutlined"
                      className={selectedProfileId === profile.id ? 'active' : ''}
                      profile={profile}
                      id={`emtcard-${profile.id}`}
                      onMouseEnter={() => {
                        handleCardMouseEnter(profile.id)
                      }}
                      onMouseLeave={handleCardMouseLeave}
                      sx={{ cursor: 'pointer', '& .css-1je5pk2-MuiAvatar-root': { minWidth: '100px!important' } }}
                      onClick={() => {
                        handleCardClick(profile.id)
                      }}
                      blankPage
                    />
                  ))}
            </Box>
          </EmtDrawer>
          {/* END PROFILES */}
        </Box>
      </MapFinderContainer>
    </MapFinderContainer>
  )
}

export default MapDesktop
